import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import {
  CreateOrderRequest,
  OrderResponse,
  OrderDetailsResponse,
  PaginatedOrderResponse,
  Draft,
} from '../models/order.model';
import { PlanChangeQntRequest } from '../models/plan.model';
import { environment } from '../../../environments/environment';
import { unformatCnpj } from '../utils/cnpj.util';

import { ApiService } from './api.service';
import { BffHttpClient } from './bff-http-client.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly API_URL = '/orders';
  private selectedOrderId: string | null = null;
  private orderDetailsCreditContext: {
    orderUuid: string;
    creditAnalysisRejected: boolean;
  } | null = null;
  private apiService = inject(ApiService);
  private bffHttp = inject(BffHttpClient);


  setSelectedOrderId(orderId: string): void {
    this.selectedOrderId = orderId;
  }

  getSelectedOrderId(): string | null {
    return this.selectedOrderId;
  }

  clearSelectedOrderId(): void {
    this.selectedOrderId = null;
  }

  setOrderDetailsCreditContext(
    orderUuid: string,
    creditAnalysisRejected: boolean
  ): void {
    this.orderDetailsCreditContext = { orderUuid, creditAnalysisRejected };
  }

  isCreditAnalysisRejectedForOrder(orderUuid: string): boolean {
    if (
      !this.orderDetailsCreditContext ||
      this.orderDetailsCreditContext.orderUuid !== orderUuid
    ) {
      return false;
    }
    return this.orderDetailsCreditContext.creditAnalysisRejected === true;
  }

  clearOrderDetailsCreditContext(): void {
    this.orderDetailsCreditContext = null;
  }

  createOrder(
    payload: CreateOrderRequest, 
    action?: string, 
    planUuid?: string
  ): Observable<OrderResponse> {
    
    let params = new HttpParams();

    if (action) {
      params = params.set('action', action);
    }
    if (planUuid) {
      params = params.set('planUuid', planUuid);
    }

    return this.apiService.post<OrderResponse>(this.API_URL, payload, { params });
  }

  validateOrder(payload: CreateOrderRequest): Observable<CreateOrderRequest> {
    return this.apiService.post<CreateOrderRequest>(`${this.API_URL}/validate-order`, payload);
  }

  getOrderDetailsByUuid(uuid: string): Observable<OrderDetailsResponse> {
    const url = `${this.API_URL}/details/${uuid}`;
    if (environment.useBff) {
      return this.bffHttp.get(`${url}`);
    }
    return this.apiService.get<OrderDetailsResponse>(url);
  }

  private buildCustomerOrdersPaginatedParams(params: {
    page?: number;
    size?: number;
    sortField?: string;
    sortDirection?: string;
    status?: string | string[];
    searchTerm?: string;
    contractId?: string;
  }): HttpParams {
    const page = params.page ?? 0;
    const size = params.size ?? 5;
    const sortField = params.sortField ?? 'createdAt';
    const sortDirection = params.sortDirection ?? 'desc';

    let httpParams = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', `${sortField},${sortDirection}`);

    if (params.contractId) {
      httpParams = httpParams.set('contractId', params.contractId);
    }

    if (Array.isArray(params.status)) {
      params.status
        .filter((s) => !!s)
        .forEach((s) => {
          httpParams = httpParams.append('status', s);
        });
    } else if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    const trimmedSearch = (params.searchTerm ?? '').trim();
    if (trimmedSearch) {
      httpParams = httpParams.set('searchTerm', trimmedSearch);
    }

    return httpParams;
  }

  getCustomerOrdersPaginated(
    customerUuid: string,
    cnpj: string,
    params: {
      page?: number;
      size?: number;
      sortField?: string;
      sortDirection?: string;
      status?: string | string[];
      searchTerm?: string;
      contractId?: string;
    } = {}
  ): Observable<PaginatedOrderResponse> {
    const cleanCnpj = unformatCnpj(cnpj);

    const url = `/orders/customer/${customerUuid}/${encodeURIComponent(cleanCnpj)}/paginated`;
    const httpParams = this.buildCustomerOrdersPaginatedParams(params);
    return this.apiService.get<PaginatedOrderResponse>(url, { params: httpParams });
  }

  getOrderDraftByUuid(uuid: string): Observable<Draft> {
    return this.apiService.get<Draft>(`${this.API_URL}/draft/last/${uuid}`);
  }

  createOrderDraft(payload: Draft): Observable<any> {
    return this.apiService.post<any>(`${this.API_URL}/draft`, payload);
  }

  changePlanQnt(planUuid: string, payload: PlanChangeQntRequest): Observable<any> {
    const url = `${this.API_URL}/change-quantity/${planUuid}`;
    return this.apiService.put<any>(url, payload);
  }
}
